import React, { createContext, useContext, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';

import { get, isEqual, isNil } from 'lodash';

const DrawerContext = createContext({
  drawerData: null,
  openDrawer: () => {},
  closeDrawer: () => {},
  isDrawerOpen: () => {},
});

const initialState = { name: null, data: null };

function reducer(state, action) {
  switch (get(action, 'type')) {
    case 'openDrawer': {
      const name = get(action, 'payload.name');
      const data = get(action, 'payload.data');

      return { ...state, name, data };
    }
    case 'closeDrawer': {
      return initialState;
    }
    default: {
      throw new Error(`Unhandled action type: ${get(action, 'type')}`);
    }
  }
}

export function DrawerProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const contextValue = useMemo(() => {
    const openDrawer = (name, data) => {
      dispatch({ type: 'openDrawer', payload: { name, data } });
    };

    const closeDrawer = () => {
      dispatch({ type: 'closeDrawer', payload: {} });
    };

    const isDrawerOpen = name => {
      return isEqual(name, get(state, 'name'));
    };

    return {
      drawerData: get(state, 'data'),
      openDrawer,
      closeDrawer,
      isDrawerOpen,
    };
  }, [state]);

  return <DrawerContext.Provider value={contextValue}>{children}</DrawerContext.Provider>;
}

DrawerProvider.propTypes = {
  children: PropTypes.node,
};

DrawerProvider.defaultProps = {
  children: null,
};

export function useDrawer() {
  const context = useContext(DrawerContext);
  if (isNil(context)) {
    throw new Error('useDrawer must be used within a DrawerProvider');
  }
  return context;
}
